import CobraSpinner from '../shared/CobraSpinner';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  Flex,
  StatNumber,
  StatHelpText,
  Select,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  VStack,
  HStack,
  useToast,
  
  Center} from '@chakra-ui/react';
import { fetchCourses } from '../services/courseService';
import { exportStudentFeedback, fetchStudentFeedbackStats, listStudentFeedback } from '../services/studentFeedbackService';
import { SidebarProvider, useSidebar } from '../utils/SidebarContext';
import SidebarAdmin from '../shared/Sidebar2';
import { useColorModeValue, useBreakpointValue } from '@chakra-ui/react';


const AdminStudentFeedback = () => {
  const { isOpen } = useSidebar();
  const toast = useToast();
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [q, setQ] = useState('');
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const bgColor = useColorModeValue('#f8f9fa', 'gray.900');
  const contentMarginRight = useBreakpointValue({ base: '0px', md: isOpen ? '250px' : '100px' });
  const loadStats = async (params = {}) => {
    const data = await fetchStudentFeedbackStats(params);
    setStats(data);
  };

  const loadRows = async (params = {}) => {
    const merged = {
      page: params.page ?? currentPage,
      limit: params.limit ?? limit,
      courseId: params.courseId ?? (courseId || undefined),
      q: params.q ?? (q || undefined),
    };
    const data = await listStudentFeedback(merged);
    setRows(data.data || []);
    setPagination(data.pagination || { currentPage: 1, totalPages: 1 });
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const allCourses = await fetchCourses();
        setCourses(allCourses || []);
        await loadStats({});
        await loadRows({ page: 1, limit });
      } catch (e) {
        toast({ title: 'تعذر تحميل البيانات', status: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleFilter = async () => {
    const params = { page: 1, limit, courseId: courseId || undefined, q: q || undefined };
    try {
      setLoading(true);
      await loadStats(params);
      await loadRows(params);
    } catch (e) {
      toast({ title: 'تعذر تحميل البيانات', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (page) => {
    if (page < 1 || page > (pagination.totalPages || 1)) return;
    setCurrentPage(page);
    try {
      setLoading(true);
      await loadRows({ page, limit });
    } catch (e) {
      toast({ title: 'تعذر تحميل البيانات', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Filters are applied only when clicking the Apply button

  const handleExport = async () => {
    try {
      const blob = await exportStudentFeedback({ courseId: courseId || undefined });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'student_feedback.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (e) {
      toast({ title: 'تعذر التصدير', status: 'error' });
    }
  };

  const overall = stats?.overall || { avgUnderstanding: 0, avgSatisfaction: 0, count: 0 };

  return (
    <Box dir="rtl" fontFamily="Tajawal, sans-serif">
      <Flex h="100vh" overflowX="hidden" flexDirection="column">
        <SidebarAdmin />
        <Box flex="1" mr={contentMarginRight} p={6} bg={bgColor} transition="margin-right 0.3s ease" overflowY="auto">
          <Heading size="lg" mb={6}>إحصائيات تقييمات الطلاب</Heading>

          <HStack spacing={4} mb={4} align="flex-end">
            <Box>
              <Text mb={2}>تصفية حسب المادة</Text>
              <Select value={courseId} onChange={(e) => setCourseId(e.target.value)} placeholder="كل المواد">
                {courses.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </Select>
            </Box>
            <Box>
              <Text mb={2}>بحث باسم الطالب</Text>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="اكتب اسم الطالب" />
            </Box>
            <Button colorScheme="blue" onClick={handleFilter}>تطبيق</Button>
            <Button onClick={handleExport}>تصدير Excel</Button>
          </HStack>

          {loading ? (
            <Center py={20}>
              <CobraSpinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
            </Center>
          ) : (
            <>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
                <Stat borderWidth="1px" borderRadius="md" p={4} bg="white">
                  <StatLabel>متوسط نسبة الفهم</StatLabel>
                  <StatNumber>{overall.avgUnderstanding?.toFixed(1) || 0}%</StatNumber>
                  <StatHelpText>لكل التقييمات</StatHelpText>
                </Stat>
                <Stat borderWidth="1px" borderRadius="md" p={4} bg="white">
                  <StatLabel>متوسط نسبة الرضا</StatLabel>
                  <StatNumber>{overall.avgSatisfaction?.toFixed(2) || 0}/5</StatNumber>
                  <StatHelpText>لكل التقييمات</StatHelpText>
                </Stat>
                <Stat borderWidth="1px" borderRadius="md" p={4} bg="white">
                  <StatLabel>عدد السجلات</StatLabel>
                  <StatNumber>{overall.count || 0}</StatNumber>
                  <StatHelpText>إجمالي</StatHelpText>
                </Stat>
              </SimpleGrid>

              <Heading size="md" mb={3}>جميع التقييمات</Heading>
              <Box overflowX="auto" borderWidth="1px" borderRadius="md" bg="white">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>الطالب</Th>
                      <Th>المادة</Th>
                      <Th>الفهم %</Th>
                      <Th>الرضا /5</Th>
                      <Th>الملاحظة</Th>
                      <Th>التاريخ</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((r) => (
                      <Tr key={r._id}>
                        <Td>{r.user?.username}</Td>
                        <Td>{r.course?.name}</Td>
                        <Td>{r.understandingPercent}</Td>
                        <Td>{r.platformSatisfaction}</Td>
                        <Td maxW="400px" whiteSpace="normal">{r.suggestion}</Td>
                        <Td>{new Date(r.createdAt).toLocaleString()}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              <Flex justify="space-between" align="center" mt={4}>
                <HStack spacing={2}>
                  <Button size="sm" onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage <= 1}>
                    السابق
                  </Button>
                  <Text fontSize="sm">
                    صفحة {pagination.currentPage || 1} من {pagination.totalPages || 1}
                  </Text>
                  <Button size="sm" onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage >= (pagination.totalPages || 1)}>
                    التالي
                  </Button>
                </HStack>
                <HStack spacing={2}>
                  <Text fontSize="sm">عناصر في كل صفحة:</Text>
                  <Select size="sm" width="80px" value={limit} onChange={async (e) => {
                    const newLimit = parseInt(e.target.value, 10) || 20;
                    setLimit(newLimit);
                    await handlePageChange(1);
                  }}>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </Select>
                </HStack>
              </Flex>
            </>
          )}
        </Box>
      </Flex> </Box>);
};
const AdminStudentFeedbackpage = () => {
  return (
    <AdminStudentFeedback />
  );
};

export default AdminStudentFeedbackpage;



